import React, { useCallback, useEffect, useState } from "react";
import Drawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import { toast } from "react-toastify";

import { ApiURLs } from "../../constant/ApiUrls";
import { fetchData, patchData, postData } from "../../services/api";
import { cur } from "../../constant/Constants";

const money = (v) =>
  `${cur}${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const shortDate = (v) => (v ? new Date(v).toLocaleDateString() : "-");

const INVOICE_COLOUR = {
  paid: "success",
  open: "info",
  overdue: "error",
  void: "default",
  refunded: "warning",
  draft: "default",
};

const Stat = ({ label, value, alert }) => (
  <Paper sx={{ p: 1.5, flex: "1 1 140px", minWidth: 140, borderLeft: `4px solid ${alert ? "#d32f2f" : "#1976d2"}` }}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="h6" sx={{ fontWeight: 600, color: alert ? "#d32f2f" : "inherit" }}>{value}</Typography>
  </Paper>
);

/**
 * One dealer's billing: their invoices, what is paid and what is not, every
 * payment they have made, every credit control notice raised against them, and
 * the switch that turns their account access off.
 *
 * The switch is deliberately not a one-click toggle. Turning a dealer off stops
 * them logging in, which for a paying business is the sort of thing that should
 * take two deliberate actions rather than a stray tap on a phone.
 */
export default function DealerBillingPanel({ userId, open, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmAccess, setConfirmAccess] = useState(null);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [newInvoice, setNewInvoice] = useState({ amount: "", description: "" });
  const [noticeBody, setNoticeBody] = useState(null);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const res = await fetchData(`${ApiURLs.BILLING_DEALER}/${userId}`);
      setData(res.status === 1 ? res.data : null);
    } catch (e) {
      setData(null);
    }
    setLoading(false);
  }, [userId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const applyAccess = async (enabled) => {
    setConfirmAccess(null);
    try {
      const res = await patchData(`${ApiURLs.BILLING_DEALER}/${userId}/access`, { enabled });
      if (res.status === 1) { await load(); if (onChanged) onChanged(); }
    } catch (e) { /* patchData already surfaces the message */ }
  };

  const setInvoiceStatus = async (invoiceId, status) => {
    try {
      const res = await patchData(`${ApiURLs.BILLING_INVOICES}/${invoiceId}`, { status });
      if (res.status === 1) { await load(); if (onChanged) onChanged(); }
    } catch (e) { /* handled */ }
  };

  const addInvoice = async () => {
    const amount = Number(newInvoice.amount);
    if (!Number.isFinite(amount) || amount <= 0) { toast.error("Enter an amount above zero"); return; }
    if (!newInvoice.description.trim()) { toast.error("Enter what the invoice is for"); return; }
    try {
      const res = await postData(
        { user_id: userId, amount, description: newInvoice.description.trim() },
        ApiURLs.BILLING_INVOICES
      );
      if (res.status === 1) {
        setInvoiceOpen(false);
        setNewInvoice({ amount: "", description: "" });
        await load();
        if (onChanged) onChanged();
      }
    } catch (e) { /* handled */ }
  };

  const openNotice = async (id) => {
    try {
      const res = await fetchData(`${ApiURLs.BILLING_NOTIFICATIONS}/${id}`);
      if (res.status === 1) setNoticeBody(res.data);
    } catch (e) { /* handled */ }
  };

  const sendNotice = async (id) => {
    try {
      const res = await postData({}, `${ApiURLs.BILLING_NOTIFICATIONS}/${id}/send`);
      if (res.status === 1) { setNoticeBody(null); await load(); }
    } catch (e) { /* handled */ }
  };

  const d = data && data.dealer;

  return (
    <>
      <Drawer anchor="right" open={open} onClose={onClose}
        PaperProps={{ sx: { width: { xs: "100%", md: 900 }, p: 2 } }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {d ? d.seller : "Dealer billing"}
          </Typography>
          <IconButton onClick={onClose} aria-label="Close" sx={{ fontSize: 22, lineHeight: 1 }}>&times;</IconButton>
        </Box>

        {loading && <Box sx={{ p: 4, textAlign: "center" }}><CircularProgress /></Box>}

        {!loading && !data && (
          <Typography color="text.secondary">Nothing to show for this account.</Typography>
        )}

        {!loading && data && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* ------------------------------------------------ who they are */}
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", alignItems: "center" }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Account number</Typography>
                  <Typography sx={{ fontWeight: 600 }}>{d.account_number || "not issued yet"}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Email</Typography>
                  <Typography>{d.billing_email || d.email || "-"}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Tier</Typography>
                  <Typography>
                    {d.tier_name || "none"}
                    {d.tier_price > 0 ? ` - ${money(d.tier_price)}/month` : " - free"}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Billing</Typography>
                  <Box><Chip size="small" label={d.billing_state}
                    color={d.billing_state === "Active" ? "success" : d.billing_state === "Free tier" ? "default" : "error"} /></Box>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Live ads</Typography>
                  <Typography>{d.live_ads}</Typography>
                </Box>
                {Number(d.credit_control_stage) > 0 && (
                  <Chip size="small" color="error" label="Flagged for credit control" />
                )}
              </Box>

              <Divider sx={{ my: 1.5 }} />

              <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                <Tooltip title="Switching this off stops them signing in. Their adverts stay on the site.">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!d.access_enabled}
                        onChange={(e) => setConfirmAccess(e.target.checked)}
                      />
                    }
                    label={d.access_enabled ? "Account access is on" : "Account access is OFF"}
                  />
                </Tooltip>
                {!d.access_enabled && d.blocked_at && (
                  <Typography variant="caption" color="error">
                    turned off {shortDate(d.blocked_at)}
                  </Typography>
                )}
                <Box sx={{ flex: 1 }} />
                <Button size="small" variant="outlined" onClick={() => setInvoiceOpen(true)}>
                  Raise an invoice
                </Button>
              </Box>
            </Paper>

            {/* ---------------------------------------------------- the money */}
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <Stat label="Paid" value={money(data.totals.paid)} />
              <Stat label="Outstanding" value={money(data.totals.outstanding)} alert={data.totals.outstanding > 0} />
              <Stat label="Overdue" value={money(data.totals.overdue)} alert={data.totals.overdue > 0} />
              <Stat label="Invoices" value={`${data.totals.n_paid} paid / ${data.totals.n_open + data.totals.n_overdue} unpaid`} />
            </Box>

            {/* ------------------------------------------------------ invoices */}
            <Paper sx={{ p: 1 }}>
              <Typography variant="subtitle2" sx={{ px: 1, py: 0.5, fontWeight: 600 }}>Invoices</Typography>
              {data.invoices.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ px: 1, pb: 1 }}>
                  No invoices yet. Nothing has been billed to this account.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Number</TableCell>
                      <TableCell>For</TableCell>
                      <TableCell>Period</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Due</TableCell>
                      <TableCell>Paid</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.invoices.map((i) => (
                      <TableRow key={i.id} hover>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{i.invoice_number}</TableCell>
                        <TableCell>{i.description}</TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {i.period_start ? `${shortDate(i.period_start)} - ${shortDate(i.period_end)}` : "-"}
                        </TableCell>
                        <TableCell align="right">{money(i.total)}</TableCell>
                        <TableCell>
                          <Chip size="small" label={i.status} color={INVOICE_COLOUR[i.status] || "default"} />
                          {i.returned_at && (
                            <Tooltip title={`Direct Debit returned ${shortDate(i.returned_at)}: ${i.returned_reason || "no reason given"}`}>
                              <Chip size="small" sx={{ ml: 0.5 }} color="error" variant="outlined" label="returned" />
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{shortDate(i.due_date)}</TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{shortDate(i.paid_at)}</TableCell>
                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                          {i.status !== "paid" && (
                            <Button size="small" onClick={() => setInvoiceStatus(i.id, "paid")}>Mark paid</Button>
                          )}
                          {i.status !== "void" && i.status !== "paid" && (
                            <Button size="small" color="inherit" onClick={() => setInvoiceStatus(i.id, "void")}>Void</Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Paper>

            {/* ------------------------------------------------- notices sent */}
            <Paper sx={{ p: 1 }}>
              <Typography variant="subtitle2" sx={{ px: 1, py: 0.5, fontWeight: 600 }}>
                Credit control notices
              </Typography>
              {data.notifications.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ px: 1, pb: 1 }}>
                  None raised against this account.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Raised</TableCell>
                      <TableCell>Subject</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Deadline</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.notifications.map((n) => (
                      <TableRow key={n.id} hover>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{shortDate(n.created_at)}</TableCell>
                        <TableCell>{n.subject}</TableCell>
                        <TableCell align="right">{n.amount ? money(n.amount) : "-"}</TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{shortDate(n.deadline_date)}</TableCell>
                        <TableCell>
                          <Chip size="small" label={n.status}
                            color={n.status === "sent" ? "success" : n.status === "resolved" ? "default"
                              : n.status === "failed" ? "error" : "warning"} />
                          {n.escalated_at && <Chip size="small" sx={{ ml: 0.5 }} color="error" variant="outlined" label="escalated" />}
                        </TableCell>
                        <TableCell align="right">
                          <Button size="small" onClick={() => openNotice(n.id)}>Read</Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Paper>

            {/* ------------------------------------------------------ payments */}
            <Paper sx={{ p: 1 }}>
              <Typography variant="subtitle2" sx={{ px: 1, py: 0.5, fontWeight: 600 }}>Payments</Typography>
              {data.payments.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ px: 1, pb: 1 }}>
                  This account has never paid anything.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>For</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Via</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Reference</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.payments.map((p) => (
                      <TableRow key={p.id} hover>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{shortDate(p.created_at)}</TableCell>
                        <TableCell>
                          {p.description || "-"}
                          {p.promo_code && (
                            <Chip size="small" sx={{ ml: 0.5 }} variant="outlined"
                              label={`${p.promo_code} -${money(p.discount)}`} />
                          )}
                        </TableCell>
                        <TableCell align="right">{money(p.amount)}</TableCell>
                        <TableCell>{p.gateway || "-"}</TableCell>
                        <TableCell>
                          <Chip size="small" label={p.status}
                            color={/succe/i.test(p.status) ? "success" : "error"} />
                        </TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{p.transaction_id || "-"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Paper>
          </Box>
        )}
      </Drawer>

      {/* Turning access off is a two-step action on purpose. */}
      <Dialog open={confirmAccess !== null} onClose={() => setConfirmAccess(null)}>
        <DialogTitle>{confirmAccess ? "Switch access back on?" : "Switch this account off?"}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {confirmAccess
              ? "They will be able to sign in again straight away."
              : "They will not be able to sign in, and will see the message about access being temporarily limited. Their adverts stay live on the site - this only stops them getting into their account."}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAccess(null)}>Cancel</Button>
          <Button variant="contained" color={confirmAccess ? "primary" : "error"}
            onClick={() => applyAccess(confirmAccess)}>
            {confirmAccess ? "Switch on" : "Switch off"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={invoiceOpen} onClose={() => setInvoiceOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Raise an invoice</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <TextField label="What is it for" size="small" value={newInvoice.description}
            onChange={(e) => setNewInvoice({ ...newInvoice, description: e.target.value })} />
          <TextField label={`Amount (${cur})`} size="small" type="number" value={newInvoice.amount}
            onChange={(e) => setNewInvoice({ ...newInvoice, amount: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInvoiceOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={addInvoice}>Raise it</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!noticeBody} onClose={() => setNoticeBody(null)} fullWidth maxWidth="sm">
        <DialogTitle>{noticeBody && noticeBody.subject}</DialogTitle>
        <DialogContent>
          <Typography variant="caption" color="text.secondary">
            To {noticeBody && (noticeBody.to_address || "no address on the account")}
          </Typography>
          <Box sx={{ mt: 1, whiteSpace: "pre-wrap", fontFamily: "inherit", fontSize: 14, lineHeight: 1.5 }}>
            {noticeBody && noticeBody.body}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoticeBody(null)}>Close</Button>
          {noticeBody && (noticeBody.status === "queued" || noticeBody.status === "failed") && (
            <Button variant="contained" onClick={() => sendNotice(noticeBody.id)}>
              Send this now
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
